import { Component, Prop, State, h, Event, EventEmitter } from '@stencil/core';
import { AuthType, QRCodeResponse, AuthSuccessResponse, AuthErrorResponse } from './types';

@Component({
  tag: 'google-auth',
  styleUrl: 'google-auth.scss',
  shadow: true,
})
export class GoogleAuth {
  @Prop() getQrcodeUrl: string;
  @Prop() verifyCodeUrl: string;
  @Prop() accessToken: string;
  @Prop() type: AuthType = 'google';
  @Prop() phone?: string;

  @State() private qrCodeUrl: string;
  @State() private status: 'initial' | 'verifying' | 'success' | 'error' = 'initial';
  @State() private errorMessage: string = '';
  @State() private codeInputs: string[] = ['', '', '', '', '', ''];
  @State() private showQRCode: boolean = false;
  @State() private countdown: number = 0;

  private countdownTimer: number;

  @Event({
    eventName: 'authSuccess',
  })
  authSuccess: EventEmitter<AuthSuccessResponse>;

  @Event({
    eventName: 'authError',
  })
  authError: EventEmitter<AuthErrorResponse>;

  @Event({
    eventName: 'authBound'
  })
  authBound: EventEmitter<void>;

  private inputRefs: HTMLInputElement[] = [];

  componentWillLoad() {
    if (this.type === 'phone' && this.phone) {
      this.fetchQRCode();
    }
  }

  disconnectedCallback() {
    if (this.countdownTimer) {
      window.clearInterval(this.countdownTimer);
    }
  }

  private startCountdown() {
    this.countdown = 60;
    this.countdownTimer = window.setInterval(() => {
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        window.clearInterval(this.countdownTimer);
      }
    }, 1000);
  }

  private async fetchQRCode() {
    try {
      const response = await fetch(`${this.getQrcodeUrl}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(this.type === 'google' ? '获取二维码失败' : '获取验证码失败');

      const data = (await response.json()) as QRCodeResponse;
      if (data.code !== 200) {
        throw new Error(data.message || (this.type === 'google' ? '获取二维码失败' : '获取验证码失败'));
      }

      if (this.type === 'google') {
        this.qrCodeUrl = data.data.qr_img;
        this.showQRCode = true;
        this.authBound.emit();
      } else {
        this.startCountdown();
      }
    } catch (error) {
      this.errorMessage = error.message;
      this.authError.emit({
        code: 400,
        message: error.message,
      });
    }
  }

  private async verifyCode() {
    const code = this.codeInputs.join('');
    if (!code || code.length !== 6) {
      this.errorMessage = '请输入6位验证码';
      return;
    }

    this.status = 'verifying';
    try {
      const response = await fetch(`${this.verifyCodeUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.accessToken}`,
        },
        body: JSON.stringify({
          code,
          ...(this.type === 'phone' ? { phone: this.phone } : {}),
        }),
      });

      if (!response.ok) throw new Error('验证失败');

      const data = await response.json();
      if (data.code !== 200) {
        throw new Error(data.message || '验证失败');
      }
      if (data.data.is_pass) {
        this.status = 'success';
        this.authSuccess.emit({
          code: data.code,
          message: data.message,
          data: {
            is_pass: data.data.is_pass
          }
        });
      } else {
        this.status = 'error';
        this.errorMessage = '验证失败';
        this.authError.emit({
          code: 400,
          message: '验证失败',
        });
      }
    } catch (error) {
      this.status = 'error';
      this.errorMessage = error.message;
      this.authError.emit({
        code: 400,
        message: error.message,
      });
    }
  }

  private handleInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // 如果输入的不是数字，清空输入框
    if (!/^\d*$/.test(value)) {
      input.value = '';
      return;
    }

    // 只取最后一个字符
    const lastChar = value.slice(-1);
    input.value = lastChar;

    // 更新当前输入框的值
    this.codeInputs = [...this.codeInputs.slice(0, index), lastChar, ...this.codeInputs.slice(index + 1)];

    // 自动聚焦下一个输入框
    if (lastChar && index < 5) {
      this.inputRefs[index + 1]?.focus();
    }

    // 当所有输入框都填写完成时自动验证
    if (this.codeInputs.every(v => v) && this.codeInputs.join('').length === 6) {
      this.verifyCode();
    }
  }

  private handleKeyDown(index: number, event: KeyboardEvent) {
    // 阻止所有按键，只允许以下情况：
    // 1. 数字键
    // 2. 退格键
    // 3. Delete 键
    // 4. 方向键
    // 5. Tab 键
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
      return;
    }

    // 处理退格键
    if (event.key === 'Backspace') {
      if (!this.codeInputs[index] && index > 0) {
        // 当前输入框为空且按下删除键时，聚焦到前一个输入框并清空其值
        this.inputRefs[index - 1]?.focus();
        this.codeInputs = [...this.codeInputs.slice(0, index - 1), '', ...this.codeInputs.slice(index)];
      } else {
        // 清空当前输入框
        this.codeInputs = [...this.codeInputs.slice(0, index), '', ...this.codeInputs.slice(index + 1)];
      }
    }
  }

  private handlePaste = (event: ClipboardEvent) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const numbers = pastedText.replace(/\D/g, '').slice(0, 6).split('');

    this.codeInputs = [...numbers, ...Array(6 - numbers.length).fill('')];

    // 聚焦到最后一个填充的输入框的下一个
    const nextEmptyIndex = numbers.length;
    if (nextEmptyIndex < 6) {
      this.inputRefs[nextEmptyIndex]?.focus();
    }

    // 如果粘贴的内容正好是6位数字，触发验证
    if (numbers.length === 6) {
      this.verifyCode();
    }
  };

  render() {
    const maskPhone = (phone: string) => {
      return phone?.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
    };

    return (
      <div class="google-auth-container">
        <div class="auth-header">
          <div class="lock-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 1C8.676 1 6 3.676 6 7v2H4v14h16V9h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v2H8V7c0-2.276 1.724-4 4-4z" />
            </svg>
          </div>
        </div>

        <div class="verification-section">
          {this.type === 'google' ? (
            <p>当前身份已开启登录保护，请输入Google Authenticator上的安全码</p>
          ) : (
            <p>当前身份已开启登录保护，请输入发送至 {maskPhone(this.phone)} 的验证码</p>
          )}

          <div class="code-input-group">
            {Array(6)
              .fill(null)
              .map((_, index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={this.codeInputs[index]}
                  onInput={e => this.handleInput(index, e)}
                  onKeyDown={e => this.handleKeyDown(index, e)}
                  onPaste={this.handlePaste}
                  disabled={this.status === 'verifying' || this.status === 'success'}
                  ref={el => (this.inputRefs[index] = el as HTMLInputElement)}
                />
              ))}
          </div>

          {this.type === 'google' && !this.showQRCode && (
            <button class="get-qr-button" onClick={() => this.fetchQRCode()}>
              获取二维码
            </button>
          )}

          {this.type === 'phone' && (
            <button
              class="get-code-button"
              onClick={() => this.fetchQRCode()}
              disabled={this.countdown > 0}
            >
              {this.countdown > 0 ? `重新获取(${this.countdown}s)` : '获取验证码'}
            </button>
          )}

          {this.type === 'google' && this.showQRCode && this.qrCodeUrl && (
            <div class="qr-code">
              <img src={this.qrCodeUrl} alt="Google Authenticator QR Code" />
            </div>
          )}
        </div>

        {this.errorMessage && <div class="error-message">{this.errorMessage}</div>}

        {this.status === 'success' && <div class="success-message">验证成功！</div>}
      </div>
    );
  }
}
